import { cva, type VariantProps } from "class-variance-authority";
import { Image as ImageIcon } from "lucide-react";
import NextImage from "next/image";
import { cn } from "../../lib/utils";

const sectionHeaderVariants = cva(
  "flex w-full min-w-0 max-w-full items-center gap-4",
  {
    variants: {
      size: {
        default: "py-4",
        compact: "py-2",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const sectionHeaderImageVariants = cva(
  "flex-shrink-0 rounded-xl bg-muted object-cover",
  {
    variants: {
      size: {
        default: "h-[120px] w-[120px]",
        compact: "h-[70px] w-[70px]",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const sectionHeaderContentVariants = cva("flex min-w-0 flex-1 flex-col", {
  variants: {
    size: {
      default: "gap-2",
      compact: "gap-1",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const emphasisVariants = cva(
  "font-bold text-muted-foreground uppercase tracking-widest",
  {
    variants: {
      size: {
        default: "mb-1 text-xs",
        compact: "mb-0.5 text-[10px]",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

const titleVariants = cva("font-bold leading-tight tracking-tight", {
  variants: {
    size: {
      default: "text-2xl",
      compact: "text-lg",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const subtitleVariants = cva("text-muted-foreground leading-relaxed", {
  variants: {
    size: {
      default: "text-base",
      compact: "text-sm",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface SectionHeaderProps
  extends VariantProps<typeof sectionHeaderVariants> {
  className?: string;
  emphasis?: string;
  imageAlt?: string;
  imageUrl?: string;
  subtitle?: string;
  title: string;
}

export function SectionHeader({
  title,
  subtitle,
  emphasis,
  imageUrl,
  imageAlt = "",
  size = "default",
  className,
}: SectionHeaderProps) {
  const hasImage = Boolean(imageUrl);

  return (
    <div
      className={cn(sectionHeaderVariants({ size }), className)}
      data-slot="section-header"
    >
      {hasImage && (
        <div className={cn(sectionHeaderImageVariants({ size }))}>
          {imageUrl ? (
            <NextImage
              alt={imageAlt || title}
              className="h-full w-full rounded-xl object-cover"
              height={size === "compact" ? 70 : 120}
              src={imageUrl}
              width={size === "compact" ? 70 : 120}
            />
          ) : (
            <div
              aria-hidden="true"
              className="flex h-full w-full items-center justify-center"
            >
              <ImageIcon
                className={cn(
                  "text-muted-foreground",
                  size === "compact" ? "h-6 w-6" : "h-10 w-10"
                )}
              />
            </div>
          )}
        </div>
      )}

      <div className={cn(sectionHeaderContentVariants({ size }))}>
        {emphasis && (
          <p className={cn(emphasisVariants({ size }))} data-slot="emphasis">
            {emphasis}
          </p>
        )}
        <h1 className={cn(titleVariants({ size }))} data-slot="title">
          {title}
        </h1>
        {subtitle && (
          <p className={cn(subtitleVariants({ size }))} data-slot="subtitle">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
